import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { z } from "zod";

import { useFeatureIsOn } from "@/app/featureFlags";
import { BannersPlacement } from "@/components/BannersPlacement/BannersPlacement";
import { Button } from "@/components/Button";
import { PersistentDrawer } from "@/components/Drawer";
import { PromoCodeForm } from "@/components/PromoCodeForm/PromoCodeForm";
import { SearchDataMissingError } from "@/errors/SearchDataMissingError";
import { HotelAndRoomCard } from "@/features/hotels/components/hotel/HotelAndRoomCard";
import { MainTravellerSelectionCard } from "@/features/hotels/components/hotel/MainTravellerSelectionCard";
import { OrderSummaryCard } from "@/features/hotels/components/hotel/OrderSummaryCard";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelTravellersSelectionContext } from "@/features/hotels/contexts/HotelTravellersSelectionContext";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import { PromoCodeTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { BannersPlacementSlug } from "@/types/enums";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/summary",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

const validationSchema = z.object({
  leadTraveler: z.string().min(1),
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const intl = useIntl();

  const {
    data: booking,
    availabilityToken,
    promoCode,
    setPromoCode,
  } = useHotelBookingContext();
  const { selectedTravellers, leadTravellerId, setLeadTravellerId } =
    useHotelTravellersSelectionContext();
  const { data: hotel } = useHotelDataContext();
  const { searchData } = useHotelSearchDataContext();
  const arePromoCodesEnabled = useFeatureIsOn("promo-codes");

  if (!availabilityToken || !booking || !hotel) {
    throw new SearchDataMissingError("Hotel search data is missing");
  }

  const travelDate = new Date(searchData!.dates.checkIn);
  const checkOutDate = new Date(searchData!.dates.checkOut);

  const methods = useForm<
    z.input<typeof validationSchema>,
    undefined,
    z.infer<typeof validationSchema>
  >({
    defaultValues: {
      leadTraveler: leadTravellerId || "",
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const { data: promoCodeData, validationStatus: promoCodeValidationStatus } =
    usePromoCodeValidation({
      service: "hotels",
      validationRequestData: {
        promoCode: promoCode || "",
        availabilityToken: availabilityToken || "",
      },
      onCodeApplied: (promoCode, data) => {
        rudderAnalytics.track(
          PromoCodeTrackingEvents.USER_PROMO_CODE_APPLIED_SUCCESSFULLY,
          {
            promo_code: promoCode,
            discount_amount: data.discountAmount,
            flow_type: "hotel",
          },
        );
      },
      onCodeFailure: (promoCode, errorMessage) => {
        rudderAnalytics.track(
          PromoCodeTrackingEvents.USER_PROMO_CODE_APPLICATION_FAILED,
          {
            promo_code: promoCode,
            flow_type: "hotel",
            error_message: errorMessage,
          },
        );
      },
    });

  const hotelTotalPrice = calculateBookingPrice(booking);
  const basePrice = hotelTotalPrice.value;
  const totalPrice = promoCodeData?.paymentAmount || basePrice;

  return (
    <div className="px-main">
      <div className="mb-4 text-xl font-medium">
        {intl.formatMessage({
          defaultMessage: "Overview and Payment",
          description: "Page heading for the hotel booking summary",
        })}
      </div>

      <FormProvider {...methods}>
        <div className="flex w-full flex-col gap-2.5">
          <HotelAndRoomCard
            hotel={hotel}
            hotelBookingData={booking}
            rooms={searchData!.rooms}
            checkIn={travelDate}
            checkOut={checkOutDate}
          />

          <MainTravellerSelectionCard
            travelDate={travelDate}
            selectedTravellers={selectedTravellers}
          />

          <BannersPlacement
            slug={BannersPlacementSlug.CHECKOUT}
            analyticsContext={{
              context: "hotel",
              destination: searchData?.destination?.code,
            }}
          />

          {arePromoCodesEnabled ? (
            <PromoCodeForm
              appliedCode={promoCode || undefined}
              validationStatus={promoCodeValidationStatus}
              onCodeApply={({ code }) => {
                rudderAnalytics.track(
                  PromoCodeTrackingEvents.USER_SUBMIT_PROMO_CODE,
                  { promo_code: code, flow_type: "hotel" },
                );

                setPromoCode(code);
              }}
              onCodeRemove={({ code, isApplied }) => {
                if (isApplied) {
                  rudderAnalytics.track(
                    PromoCodeTrackingEvents.USER_REMOVE_PROMO_CODE,
                    {
                      promo_code: code,
                      discount_amount_removed: promoCodeData?.result
                        ? (promoCodeData.discountAmount ?? null)
                        : null,
                      flow_type: "hotel",
                    },
                  );
                }

                setPromoCode(null);
              }}
              onFocus={(hasAppliedPromoCode) => {
                if (!hasAppliedPromoCode) {
                  rudderAnalytics.track(
                    PromoCodeTrackingEvents.USER_SELECTED_PROMO_CODE,
                    { flow_type: "hotel" },
                  );
                }
              }}
            />
          ) : null}

          <OrderSummaryCard
            basePrice={basePrice}
            promoCodeDiscount={promoCodeData?.discountAmount}
            totalPrice={totalPrice}
          />
        </div>
      </FormProvider>

      <PersistentDrawer>
        <div className="flex h-full flex-col gap-4">
          <div className="flex items-center justify-between gap-4 text-text-primary">
            <div>
              <div className="text-sm font-semibold">
                {intl.formatMessage({
                  defaultMessage: "Total price",
                  description: "Label for the total booking price",
                })}
              </div>
              <div className="text-xs text-text-placeholder">
                {intl.formatMessage({
                  defaultMessage: "Include taxes and charges",
                  description: "Total booking price description",
                })}
              </div>
            </div>

            <div className="text-base font-semibold">
              {formatCurrency(totalPrice)}
            </div>
          </div>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit((data) => {
              setLeadTravellerId(data.leadTraveler);
              navigate({ to: "/hotels/$id/payment" });
            })}
            disabled={!isValid || promoCodeValidationStatus?.type === "loading"}
          >
            {intl.formatMessage({
              defaultMessage: "Continue to Payment",
              description:
                "Label for the button to proceed to the payment step after reviewing the booking summary",
            })}
          </Button>
        </div>
      </PersistentDrawer>
    </div>
  );
}
